"use client"

import type { InputRadio, RadioGroupProps } from "@jobteaser/spark/components/RadioGroup"
import type { ChangeEvent, FunctionComponent, MouseEvent, PropsWithChildren } from "react"

import { clsx } from "clsx"
import { useState } from "react"

import { History } from "@jobteaser/spark/components/Icons/History"
import { RadioGroup } from "@jobteaser/spark/components/RadioGroup"

import { useJobAdsFilterNavigation } from "@/modules/jobAds/client/hooks/useJobAdsFilterNavigation"
import { useSecondaryFilters } from "@/modules/jobAds/client/hooks/useSecondaryFilters"
import { FilterButton } from "@/modules/landingSearch/components/FilterButton/FilterButton"
import { Dropdown } from "@/modules/layouts/components/Dropdown/Dropdown"

import styles from "./PublicationDateFilterShortcut.module.css"

type PublicationDateFilterShortcutProps = PropsWithChildren<{
  className?: string
  options: RadioGroupProps["items"]
}>

export const PublicationDateFilterShortcut: FunctionComponent<PublicationDateFilterShortcutProps> = ({
  children,
  className,
  options,
}) => {
  const { secondaryFilters } = useSecondaryFilters()
  const publicationDate = secondaryFilters.publicationDate[0]?.value

  const publicationDateFromSearchParams = options.find(option => option.value === publicationDate)
  const selectedPublicationDate = publicationDateFromSearchParams || options[0]

  const { navigateWithUpdatedQueryParams } = useJobAdsFilterNavigation()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const handleClick = (event: MouseEvent): void => {
    event.stopPropagation()
    setIsDropdownOpen(!isDropdownOpen)
  }

  const handleClose = (selectedOption: InputRadio): void => {
    const selectedOptionValue = selectedOption.value

    navigateWithUpdatedQueryParams({
      availableFilters: {
        publication_date: selectedOptionValue === "no-time-limit" ? "" : selectedOptionValue,
      },
    })

    setIsDropdownOpen(false)
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const selectedOption = options.find(option => option.value === event.target.value)

    if (selectedOption) {
      handleClose(selectedOption)
    }
  }

  return (
    <div className={clsx(className, styles.main)}>
      <FilterButton
        data-testid="job-ads-secondary-filters-publication-date-button"
        icon={History}
        isActive={selectedPublicationDate.value !== "no-time-limit"}
        onClick={handleClick}
      >
        {selectedPublicationDate.value === "no-time-limit" ? children : selectedPublicationDate.label}
      </FilterButton>
      {isDropdownOpen && (
        <Dropdown
          className={styles.dropdown}
          onClose={() => {
            handleClose(selectedPublicationDate)
          }}
        >
          <RadioGroup
            defaultValue={selectedPublicationDate.value}
            items={options}
            label=""
            name="publication-date-options"
            onChange={handleChange}
          />
        </Dropdown>
      )}
    </div>
  )
}
